import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { storySteps } from '../../data/productContent';
import { useActiveStep } from '../../hooks/useActiveStep';
import type { ProductImage } from '../../types/content';
function StoryImage({
  image
}: {
  image: ProductImage;
}) {
  if (image.crop) {
    const {
      x,
      y,
      width,
      height,
      sourceWidth,
      sourceHeight
    } = image.crop;
    return <svg role="img" aria-label={image.alt} data-story-photo-crop viewBox={`${x} ${y} ${width} ${height}`} width={width} height={height} overflow="hidden" className="h-auto max-h-full w-full" style={{
      maxWidth: `min(${image.nativeWidth}px, 100%)`
    }} data-id="element-250" data-mp="cn6ojre22486">
        <title data-id="element-251" data-mp="gpv046u5lp8u">{image.alt}</title>
        <image href={image.src} width={sourceWidth} height={sourceHeight} data-id="element-252" data-mp="1x1ql84tkavh1" />
      </svg>;
  }
  return <img src={image.src} alt={image.alt} className="max-h-full w-auto object-contain" style={{
    maxWidth: `min(${image.nativeWidth}px, 100%)`
  }} data-id="element-253" data-mp="1s3f91nfso8vn" />;
}
export function ScrollStory() {
  const {
    active,
    trackRef,
    stageRef,
    goToStep,
    isPinned,
    stickyTop,
    stageHeight,
    trackHeight
  } = useActiveStep(storySteps.length);
  const reduceMotion = useReducedMotion();
  const current = storySteps[active];
  return <section aria-labelledby="story-title" className="border-t border-line" data-id="element-254" data-mp="2oss6b6b2aax2">
      <div className="mx-auto max-w-page px-5 py-16 md:px-10 lg:py-20" data-id="element-255" data-mp="1r6l2vzqhuoma">
        <div className="max-w-2xl" data-id="element-256" data-mp="1w4d2zh1alh1k">
          <h2 id="story-title" className="font-display text-4xl font-medium leading-tight text-ink md:text-[52px]" data-id="element-257" data-mp="1w5eipyt96rpm">
            Designed around ease of use.
          </h2>
          <p className="mt-4 text-[17px] leading-relaxed text-ink-muted" data-id="element-258" data-mp="25uy2r7spcrm2">
            From fit to controls, a closer look at how the mask comes together.
          </p>
        </div>

        {/* One pinned composition; native page progress changes its image and active point together. */}
        <div ref={trackRef} data-scroll-story-track className="relative mt-10" style={{
        display: isPinned ? 'block' : 'none',
        height: trackHeight
      }} data-id="element-259" data-mp="15d7bdlv7w1pw">
          <div ref={stageRef} data-scroll-story-stage data-active-step={active} className="sticky grid grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] items-stretch gap-12 xl:gap-20" style={{
          top: stickyTop,
          height: stageHeight
        }} data-id="element-260" data-mp="1f2z7ikiyt2wh">
            <div className="relative min-h-0 overflow-hidden rounded-md border border-line bg-surface" data-id="element-261" data-mp="id9fzo5vt17g">
              <AnimatePresence initial={false} data-id="element-262" data-mp="wdagh2i8i6me">
                <motion.div key={current.image.id} initial={reduceMotion ? false : {
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: reduceMotion ? 0 : 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="absolute inset-0 flex items-center justify-center p-8 xl:p-10" data-scroll-story-image data-id="element-263" data-mp="we2cgefqu6d7">
                  <StoryImage image={current.image} data-id="element-264" data-mp="3pqm008l80bqt" />
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex min-h-0 flex-col justify-center" data-id="element-265" data-mp="3brmc535j80vp">
              <p className="mb-5 text-[13px] font-medium tracking-[0.08em] text-bronze-deep" data-id="element-266" data-mp="3gpw6xcncgzgs">
                {String(active + 1).padStart(2, '0')} / {String(storySteps.length).padStart(2, '0')}
              </p>
              <ol className="border-t border-line-strong" data-id="element-267" data-mp="c9cz83wjcxsf">
                {storySteps.map((step, index) => {
                const isActive = index === active;
                return <li key={step.id} data-story-point={index} className="border-b border-line" data-id="element-268" data-mp="3h1kvgep50st" data-mpi={index}>
                      <h3 data-id="element-269" data-mp="r75b8m2vj41v">
                        <button type="button" onClick={() => goToStep(index)} aria-current={isActive ? 'step' : undefined} aria-expanded={isActive} aria-controls={`story-point-${step.id}`} className={`grid min-h-[64px] w-full grid-cols-[28px_minmax(0,1fr)] items-center gap-4 py-3 text-left transition-colors ${isActive ? 'text-ink' : 'text-ink-muted'}`} data-id="element-270" data-mp="r6m13rjgtjye">
                          <span className={`text-[14px] ${isActive ? 'text-bronze-deep' : 'text-ink-muted'}`} data-id="element-271" data-mp="3m8gc8qe5qjh8">{index + 1}</span>
                          <span className="font-display text-[28px] font-medium leading-tight xl:text-[32px]" data-id="element-272" data-mp="3i85z8try6ssv">{step.title}</span>
                        </button>
                      </h3>
                      <div id={`story-point-${step.id}`} hidden={!isActive} className="pl-11" data-id="element-273" data-mp="38i35usgwzcu0">
                        <p className="max-w-md pb-5 text-[16px] leading-relaxed text-ink-muted" data-id="element-274" data-mp="9rji7vp93o24">{step.body}</p>
                      </div>
                    </li>;
              })}
              </ol>
            </div>
          </div>
        </div>

        {/* All four points remain in document order on mobile/tablet and short screens. */}
        <ol data-scroll-story-stacked className="mt-10 space-y-12" style={{
        display: isPinned ? 'none' : undefined
      }} data-id="element-275" data-mp="hcwx2bbb0ttk">
          {storySteps.map((step, index) => <li key={step.id} className="grid gap-6 md:grid-cols-2 md:items-center md:gap-10" data-id="element-276" data-mp="qca5d0nozccp" data-mpi={index}>
              <div className="flex aspect-square max-h-[420px] items-center justify-center overflow-hidden rounded-md border border-line bg-surface p-6" data-id="element-277" data-mp="v9siz4cnmfr1">
                <StoryImage image={step.image} data-id="element-278" data-mp="150n4g6i2tbbf" />
              </div>
              <div className="border-l-2 border-bronze pl-5" data-id="element-279" data-mp="1516k1gk97nma">
                <span className="text-[14px] font-medium text-bronze-deep" data-id="element-280" data-mp="19yg7wrrzvr80">{index + 1} / {storySteps.length}</span>
                <h3 className="mt-2 font-display text-3xl font-medium text-ink md:text-4xl" data-id="element-281" data-mp="1e2ebpfs9lq7e">{step.title}</h3>
                <p className="mt-3 max-w-md text-[16px] leading-relaxed text-ink-muted" data-id="element-282" data-mp="1j0pljqik80pm">{step.body}</p>
              </div>
            </li>)}
        </ol>
      </div>
    </section>;
}